import Cookies from 'js-cookie'
import { computed, type ComputedRef, onBeforeUnmount } from 'vue'
import type { CommonContextStore } from 'src/types/context'
import useContextStore from 'src/store/useContextStore'

const FLAVOUR_COOKIE = 'flavour'

export default function useFlavour(isMobile: ComputedRef<boolean> | undefined): {
  isMobileInitial: ComputedRef<boolean>
  changeFlavour: (_flavour: 'full' | 'mobile') => void
} {
  const { context }: CommonContextStore = useContextStore()

  const isMobileInitial = computed(() => (context.request.GET.is_mobile_initial
    ? context.request.GET.is_mobile_initial === 'true'
    : !!isMobile?.value))

  /**
   * Текущая версия отличается от исходной, то есть её переключили в превью-меню.
   * Без `is_mobile_initial` в URL исходной считается текущая версия, поэтому
   * удаление параметра из адреса само сбрасывает признак переключения.
   */
  const isFlavourSwitched = computed(() => !!isMobile?.value !== isMobileInitial.value)

  function removeFlavourCookie() {
    try {
      Cookies.remove(FLAVOUR_COOKIE, { path: window.location.pathname })
    } catch { /* empty */ }
  }

  /**
   * Ставит куку для следующего запроса этого же адреса.
   *
   * Кука снимается при выгрузке страницы, поэтому после каждой загрузки её
   * нужно поставить заново, иначе перезагрузка вернёт версию по умолчанию.
   * Если версию не переключали, кука не нужна: сервер определит её сам.
   */
  function applyFlavourCookie() {
    if (!isFlavourSwitched.value) {
      removeFlavourCookie()

      return
    }

    try {
      Cookies.set(FLAVOUR_COOKIE, isMobile?.value ? 'mobile' : 'full', { path: window.location.pathname })
    } catch { /* empty */ }
  }

  async function changeFlavour(flavour: 'full' | 'mobile') {
    try {
      if (flavour === Cookies.get(FLAVOUR_COOKIE)) {
        return
      }

      const url = new URL(window.location.href)

      if (!url.searchParams.has('is_mobile_initial')) {
        url.searchParams.set('is_mobile_initial', isMobile?.value ? 'true' : 'false')
      }

      Cookies.set(FLAVOUR_COOKIE, flavour, { path: url.pathname })

      window.history.replaceState(window.history.state, '', url.href)
      window.location.reload()
    } catch (error) { /* empty */ }
  }

  if (typeof window !== 'undefined') {
    applyFlavourCookie()

    /**
     * `pagehide` срабатывает при закрытии таба и переходе на другой адрес,
     * в отличие от `onBeforeUnmount`: полную выгрузку страницы Vue не отслеживает.
     *
     * Браузер выгружает страницу уже после того, как ушёл запрос следующей
     * загрузки, поэтому перезагрузка успевает отправить куку и сохраняет
     * выбранную версию, а после ухода со страницы кука в браузере не остаётся.
     */
    window.addEventListener('pagehide', removeFlavourCookie)

    // Возврат из bfcache не перезапускает setup — куку нужно поставить заново
    window.addEventListener('pageshow', applyFlavourCookie)
  }

  onBeforeUnmount(() => {
    if (typeof document === 'undefined') {
      return
    }

    window.removeEventListener('pagehide', removeFlavourCookie)
    window.removeEventListener('pageshow', applyFlavourCookie)
    removeFlavourCookie()

    if (isMobile?.value && !isMobileInitial.value) {
      document.body.style.margin = '0'
      document.body.style.maxWidth = 'initial'
    }
  })

  return {
    isMobileInitial,
    changeFlavour,
  }
}
